import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const FORM_ADDR = "https://readdy.ai/api/form/da0kn5m859p3u981e3sg";

const testimonials = [
  {
    name: "夜猫子小周",
    role: "夜读党 · 会员3年",
    quote: "夜间模式真的救了我,每晚躲在被窝里追漫也不怕伤眼睛,翻页特别顺滑,体验绝了。",
    avatar: "ri-user-6-fill",
  },
  {
    name: "漫画收藏家",
    role: "资深读者 · 2.8万收藏",
    quote: "横屏看高清原图太震撼了,每一格画面的细节都清清楚楚,和正版体验几乎没差别。",
    avatar: "ri-user-5-fill",
  },
  {
    name: "青柠汽水",
    role: "学生党 · 每日打卡",
    quote: "阅读模式切换很自由,上下滑动、翻页、双页横屏都能一键切换,追漫超省心。",
    avatar: "ri-user-3-fill",
  },
  {
    name: "面包超人",
    role: "老漫迷 · 关注3年",
    quote: "漫画岛的内容更新很快,分类也很清晰,想看的类型一下就能找到,体验很棒。",
    avatar: "ri-user-4-fill",
  },
];

const stats = [
  { value: "1280+", label: "漫画作品" },
  { value: "9600万", label: "累计人气" },
  { value: "320万", label: "注册读者" },
  { value: "4.9/5", label: "读者评分" },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于漫画岛",
  description: "了解漫画岛品牌故事、用户口碑与联系方式。",
  breadcrumb: {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://rejqj97.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们" },
    ],
  },
};

export default function About() {
  const [status, setStatus] = useState<{ type: "idle" | "success" | "error"; msg: string }>({
    type: "idle",
    msg: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // 反垃圾蜜罐:若被自动填充,不提交但给出正常反馈
    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus({ type: "success", msg: "感谢你的留言,我们会尽快与你联系。" });
      form.reset();
      return;
    }

    data.delete("website_alt");

    setSending(true);
    setStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const code = parsed?.code;
      const msgText =
        typeof meta.message === "string"
          ? meta.message
          : typeof parsed?.message === "string"
            ? String(parsed.message)
            : typeof meta.detail === "string"
              ? String(meta.detail)
              : responseText;

      const isSpam = /spam|form data is spam/i.test(msgText);

      if (res.ok && code === "OK" && !isSpam) {
        setStatus({ type: "success", msg: "感谢你的留言,我们会尽快与你联系。" });
        form.reset();
      } else {
        setStatus({ type: "error", msg: isSpam ? "提交失败,请稍后再试。" : msgText || "提交失败,请稍后再试。" });
      }
    } catch {
      setStatus({ type: "error", msg: "网络异常,请稍后再试。" });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />

      <main className="bg-background-100 pt-16">
        <nav aria-label="面包屑" className="container-site py-4">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-700">
            <li><Link to="/" className="whitespace-nowrap hover:text-primary-500">首页</Link></li>
            <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
            <li className="whitespace-nowrap font-semibold text-primary-600" aria-current="page">关于我们</li>
          </ol>
        </nav>

        <section className="relative overflow-hidden rounded-lg">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20comic%20book%20illustration%20background%20of%20reading%20people%20and%20manga%20panels%2C%20cozy%20atmosphere%2C%20coral%20amber%20cream%20palette%2C%20stylized%20artistic%2C%20soft%20light%2C%20clean%20composition&width=1600&height=520&seq=about-hero&orientation=landscape"
            alt="漫画岛品牌故事背景"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
          <div className="container-site relative z-10 w-full py-16 md:py-20">
            <h1 className="font-heading text-3xl text-white md:text-4xl">关于漫画岛</h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
              漫画岛因「享受每一格画面」而生。我们相信,好的漫画值得用最舒适的方式去阅读。
            </p>
          </div>
        </section>

        <section className="container-site grid gap-10 py-14 md:grid-cols-2 md:py-16">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-medium text-primary-600">
              <i className="ri-quill-pen-fill"></i>
              品牌故事
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-foreground-950 md:text-3xl">
              让每一格画面都值得被好好看见
            </h2>
            <div className="mt-5 flex flex-col gap-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                漫画岛创立于 2024 年,是一支热爱漫画的年轻团队。我们深知,真正热爱漫画的人,不只在乎故事本身,更在乎阅读那一刻的沉浸与舒适。
              </p>
              <p>
                从高清原图的呈现,到横屏观看的震撼体验;从夜间模式的护眼设计,到舒适流畅的翻页动效——每一个细节,都是我们为读者精心打磨的结果。
              </p>
              <p>
                我们坚持「尊重创作、支持正版」的理念,致力于打造一个干净、纯粹、阅读体验极佳的漫画平台,陪伴每一位读者享受每一格画面。
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 content-start">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg bg-background-50 p-6 text-center">
                <p className="font-heading text-2xl text-primary-500 md:text-3xl">{s.value}</p>
                <p className="mt-1 text-sm text-foreground-700">{s.label}</p>
              </div>
            ))}
            <div className="rounded-lg bg-accent-500 p-6 text-center text-background-50">
              <p className="flex items-center justify-center gap-2 font-heading text-2xl md:text-3xl">
                <i className="ri-heart-3-fill text-accent-200"></i>
                100%
              </p>
              <p className="mt-1 text-sm text-white/90">舒适阅读承诺</p>
            </div>
            <div className="rounded-lg bg-secondary-500 p-6 text-center text-background-50">
              <p className="font-heading text-2xl md:text-3xl">24h</p>
              <p className="mt-1 text-sm text-white/90">全时陪伴阅读</p>
            </div>
          </div>
        </section>

        <section className="bg-primary-500">
          <div className="container-site py-14 md:py-16">
            <div className="text-center">
              <p className="text-xs font-medium text-white/80">读者口碑</p>
              <h2 className="mt-2 text-2xl font-semibold text-background-50 md:text-3xl">
                被 320 万读者信赖
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-lg bg-white/10 p-6 backdrop-blur-sm">
                  <div className="flex text-accent-300">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm leading-relaxed text-white/90">
                    「{t.quote}」
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-background-50">
                      <i className={`${t.avatar} text-lg`}></i>
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-white">{t.name}</span>
                      <span className="block text-xs text-white/70">{t.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="container-site grid gap-10 py-14 md:grid-cols-2 md:py-16">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-medium text-accent-600">
              <i className="ri-customer-service-2-fill"></i>
              联系我们
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-foreground-950 md:text-3xl">
              期待听到你的声音
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700">
              无论是对漫画岛的阅读体验建议、内容合作,还是遇到任何问题,都欢迎随时与我们联系。我们会在 24 小时内回复你。
            </p>
            <ul className="mt-7 flex flex-col gap-4 text-sm text-foreground-800">
              <li className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-fill text-lg"></i>
                </span>
                <div>
                  <p className="font-semibold">邮箱</p>
                  <p className="text-foreground-600">support@comic-island.example.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-phone-fill text-lg"></i>
                </span>
                <div>
                  <p className="font-semibold">电话</p>
                  <p className="text-foreground-600">400-888-8888(工作日 9:00-18:00)</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-map-pin-fill text-lg"></i>
                </span>
                <div>
                  <p className="font-semibold">地址</p>
                  <p className="text-foreground-600">中国 · 北京 · 朝阳区</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-lg bg-background-50 p-6 md:p-8">
            <h3 className="text-lg font-semibold text-foreground-950">给我们留言</h3>
            <p className="mt-1 text-sm text-foreground-600">填写下面的表单,我们会尽快与你联系。</p>
            <form
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-4"
              data-readdy-form="da0kn5m859p3u981e3sg"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-900">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="例如:小明"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-900">联系邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-900">留言主题</label>
                <select
                  id="topic"
                  name="topic"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="阅读体验反馈">阅读体验反馈</option>
                  <option value="内容建议">内容建议</option>
                  <option value="商务合作">商务合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-900">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="写下你想说的话(最多 500 字)..."
                  className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                ></textarea>
              </div>

              {/* 反垃圾隐藏字段 */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field--alt"
              />

              {status.type !== "idle" && (
                <p
                  role="status"
                  className={`rounded-md px-4 py-3 text-sm ${
                    status.type === "success"
                      ? "bg-primary-100 text-primary-800"
                      : "bg-accent-100 text-accent-800"
                  }`}
                >
                  {status.msg}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                className="w-full whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
              >
                {sending ? "提交中..." : "提交留言"}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}